import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FragmentList from './FragmentList';
import FragmentDetail from './FragmentDetail';
import CountPill from '../../../../shared/components/pill/CountPill';

function Labelled({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-muted small fw-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}

function Block({ children }) {
  return (
    <pre
      className="bg-light border rounded p-3 small mb-0"
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {children}
    </pre>
  );
}

function KeyValues({ detail, omit }) {
  const entries = Object.entries(detail || {}).filter(
    ([key, value]) => !omit.includes(key) && value != null && typeof value !== 'object'
  );
  if (entries.length === 0) return null;
  return (
    <dl className="row small mb-0">
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className="col-sm-4 text-muted fw-normal">{key.replace(/_/g, ' ')}</dt>
          <dd className="col-sm-8 mb-1">{String(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function QueryDetail({ step }) {
  return (
    <>
      <Labelled label="Query sent to retrieval"><Block>{step.detail.query}</Block></Labelled>
      <KeyValues detail={step.detail} omit={['query']} />
    </>
  );
}

function EmbedDetail({ step }) {
  return (
    <>
      <Labelled label="Embedded text"><Block>{step.detail.input}</Block></Labelled>
      <KeyValues detail={step.detail} omit={['input']} />
    </>
  );
}

// Retrieve and rerank share one renderer: both produce a ranked fragment list, and treating
// them identically is what keeps "add a rerank pass" a backend-only change.
function RunAction({ label, hint, onClick, busy }) {
  if (!onClick) return null;
  return (
    <div className="d-flex align-items-center gap-2 mb-3">
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={onClick} disabled={busy}>
        {busy ? 'Running…' : label}
      </button>
      {hint && <span className="text-muted small">{hint}</span>}
    </div>
  );
}

RunAction.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  onClick: PropTypes.func,
  busy: PropTypes.bool,
};

RunAction.defaultProps = { hint: undefined, onClick: undefined, busy: false };

function FragmentsDetail({ step, citedIds, onAnswerFrom, busy }) {
  const fragments = step.detail.fragments || [];
  const [selectedId, setSelectedId] = useState(fragments[0]?.id ?? null);

  // Re-anchor when switching between steps, otherwise the previous step's selection leaks
  // into a pass that does not contain that fragment and the right pane goes blank.
  useEffect(() => {
    setSelectedId((current) =>
      fragments.some((f) => f.id === current) ? current : fragments[0]?.id ?? null
    );
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = fragments.find((f) => f.id === selectedId) || null;

  return (
    <>
      <RunAction
        label="Generate answer from these fragments"
        hint="Runs the completion on exactly this set."
        onClick={
          onAnswerFrom && fragments.length > 0
            ? () => onAnswerFrom(fragments.map((f) => f.id))
            : undefined
        }
        busy={busy}
      />
      <KeyValues detail={step.detail} omit={['fragments']} />
      {step.detail.params && (
        <div className="text-muted small mb-2">
          {Object.entries(step.detail.params).map(([k, v]) => (
            <span key={k} className="me-3">
              {k.replace(/_/g, ' ')}: <span className="font-monospace">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
      <div className="row g-3">
        <div className="col-lg-5">
          <div className="text-muted small fw-semibold mb-1">
            Fragments ({fragments.length})
          </div>
          <FragmentList
            fragments={fragments}
            selectedId={selectedId}
            citedIds={citedIds}
            onSelect={setSelectedId}
          />
        </div>
        <div className="col-lg-7">
          <FragmentDetail fragment={selected} />
        </div>
      </div>
    </>
  );
}

function AnswerDetail({ step, onAnswerFrom, busy }) {
  const fragmentIds = step.detail.fragment_ids || [];
  return (
    <>
      <RunAction
        label="Run again with the same fragments"
        hint="Same input, so any difference is the model."
        onClick={onAnswerFrom && fragmentIds.length > 0 ? () => onAnswerFrom(fragmentIds) : undefined}
        busy={busy}
      />
      <Labelled label="Reply"><Block>{step.detail.reply}</Block></Labelled>
      <Labelled label="System prompt as sent"><Block>{step.detail.system_prompt}</Block></Labelled>
      <Labelled label="Excerpt block as sent"><Block>{step.detail.excerpts_block}</Block></Labelled>
      <KeyValues
        detail={step.detail}
        omit={['reply', 'system_prompt', 'excerpts_block', 'cited_fragment_ids', 'fragment_ids']}
      />
    </>
  );
}

const RENDERERS = {
  build_query: QueryDetail,
  embed: EmbedDetail,
  retrieve: FragmentsDetail,
  rerank: FragmentsDetail,
  answer: AnswerDetail,
};

// A step type with no registered renderer falls back to its raw payload rather than throwing.
// This is deliberate and load-bearing: a new backend step shows up here usefully on the day it
// is added, so the backend is never blocked waiting for frontend work to display it.
function FallbackDetail({ step }) {
  return (
    <>
      <div className="alert alert-secondary small py-2" role="note">
        <i className="bi bi-info-circle me-1" />
        No dedicated view for step type <code>{step.type}</code> yet — showing raw output.
      </div>
      <Block>{JSON.stringify(step.detail, null, 2)}</Block>
    </>
  );
}

export default function OperationDetail({ step, citedIds, onAnswerFrom, busy }) {
  if (!step) {
    return (
      <div className="text-muted small">
        Select an operation to inspect what it produced.
      </div>
    );
  }

  const Renderer = RENDERERS[step.type] || FallbackDetail;

  // The column header already carries the step label, so this row only adds what it cannot:
  // the machine-readable type and the timing.
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <span className="text-muted small font-monospace">{step.type}</span>
        {step.duration_ms != null && (
          <CountPill>{step.duration_ms} ms</CountPill>
        )}
      </div>
      <Renderer step={step} citedIds={citedIds} onAnswerFrom={onAnswerFrom} busy={busy} />
    </div>
  );
}

OperationDetail.propTypes = {
  step: PropTypes.object,
  citedIds: PropTypes.array,
  onAnswerFrom: PropTypes.func,
  busy: PropTypes.bool,
};

OperationDetail.defaultProps = { step: null, citedIds: [], onAnswerFrom: undefined, busy: false };
